import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Smartphone, CreditCard, Loader2, CheckCircle, ArrowLeft, ExternalLink, Copy, KeyRound } from 'lucide-react';
import { formatTSh } from '@/lib/currency';
import { formatStsTokenGroups } from '@/lib/stsToken';
import { useTranslation } from 'react-i18next';

interface PaymentMethodSelectorProps {
  amount: number;
  userId?: string;
  meterId?: string;
  adminPayerId?: string;
  source?: 'tenant' | 'owner' | 'admin' | 'concierge' | 'shared_link';
  onSuccess?: (data: any) => void;
  /** Fired once when ClickPesa marks the payment completed (after PIN / card). */
  onPaymentConfirmed?: (data: any) => void;
  onError?: (error: any) => void;
}

interface PreviewData {
  activeMethods?: Array<{
    name: string;
    status: string;
    fee: number;
  }>;
  sender?: {
    accountName: string;
    accountNumber: string;
    accountProvider: string;
  };
}

interface UserProfile {
  fullName: string;
  email: string;
  phone: string;
}

interface CardQuote {
  exchangeRate: number;   // ClickPesa rate (e.g. 2500)
  effectiveRate: number;  // after margin (e.g. 2375)
  marginPct: number;      // e.g. 0.05
  cardFeePct: number;     // e.g. 0.0463
  usdBase: number;        // before fee
  usdCharged: number;     // total charged to card
}

type PaymentMethod = 'ussd' | 'card';
type Step =
  | 'method'       // choose USSD or Card
  | 'phone'        // USSD: enter phone
  | 'preview'      // USSD: confirm details
  | 'processing'   // shared: in-flight
  | 'ussd-success' // USSD: prompt sent
  | 'card-confirm' // Card: show customer info before initiating
  | 'card-success'; // Card: link opened

// Generate a unique orderReference once per payment attempt.
// The same ref is reused on retries — the DB unique constraint prevents double-charging.
function generateOrderRef(method: PaymentMethod): string {
  const prefix = method === 'card' ? 'CARD' : 'USSD';
  const ts = Date.now().toString(36).toUpperCase();
  const rnd = Math.random().toString(36).substring(2, 7).toUpperCase();
  return `${prefix}${ts}${rnd}`;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  amount,
  userId,
  meterId,
  adminPayerId,
  source,
  onSuccess,
  onPaymentConfirmed,
  onError,
}) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  // Payment flow state
  const [step, setStep] = useState<Step>('method');
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [loading, setLoading] = useState(false);

  // USSD state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [transactionResult, setTransactionResult] = useState<any>(null);

  // Card state
  const [userProfile, setUserProfile] = useState<UserProfile>({ fullName: '', email: '', phone: '' });
  const [cardPaymentLink, setCardPaymentLink] = useState<string | null>(null);
  const [cardQuote, setCardQuote] = useState<CardQuote | null>(null);

  // Post-initiate: wait for webhook + STS token / LoRaWAN downlink result
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [stsTokenNo, setStsTokenNo] = useState<string | null>(null);
  const [rechargeChannel, setRechargeChannel] = useState<string | null>(null);
  const [rechargePending, setRechargePending] = useState(false);
  const [rechargeFailed, setRechargeFailed] = useState(false);
  const confirmedNotified = useRef(false);
  const tokenNotified = useRef(false);

  // Idempotency: one orderReference per payment session, reused on retries.
  // Reset when amount changes (new payment session).
  const orderRef = useRef<string | null>(null);

  const resetPaymentSession = () => {
    orderRef.current = null;
    confirmedNotified.current = false;
    tokenNotified.current = false;
    setStep('method');
    setMethod(null);
    setPreviewData(null);
    setTransactionResult(null);
    setCardPaymentLink(null);
    setCardQuote(null);
    setPaymentConfirmed(false);
    setPaymentFailed(false);
    setStsTokenNo(null);
    setRechargeChannel(null);
    setRechargePending(false);
    setRechargeFailed(false);
  };

  useEffect(() => {
    resetPaymentSession();
  }, [amount]);

  const applyPaymentRow = (row: any) => {
    if (!row) return;
    const status = String(row.status || '').toLowerCase();
    const token = row.sts_token_no ? String(row.sts_token_no) : null;
    const recharge = String(row.recharge_status || '').toLowerCase();
    const channel = row.recharge_channel ? String(row.recharge_channel) : null;

    if (token) setStsTokenNo(token);
    if (channel) setRechargeChannel(channel);

    if (status === 'failed') {
      setPaymentFailed(true);
      setRechargePending(false);
      return;
    }

    if (status === 'completed' || status === 'success') {
      setPaymentConfirmed(true);
      setTransactionResult((prev: any) =>
        prev ? { ...prev, status: 'COMPLETED' } : { status: 'COMPLETED', ...row }
      );

      const deliveryDone =
        !!token ||
        recharge === 'confirmed' ||
        recharge === 'failed' ||
        !!channel;
      if (deliveryDone) {
        setRechargePending(false);
      } else {
        setRechargePending(true);
      }
      setRechargeFailed(recharge === 'failed' && !token);

      const downlinkOk = channel === 'chirpstack';
      const keypadFallback = !!token && channel === 'sts_token';

      if (!confirmedNotified.current) {
        confirmedNotified.current = true;
        if (downlinkOk) {
          toast({
            title: t('topup.payMethod.downlinkOkTitle'),
            description: t('topup.payMethod.downlinkOkDesc'),
            duration: 12000,
          });
        } else if (keypadFallback) {
          toast({
            title: t('topup.payMethod.downlinkFailTitle'),
            description: t('topup.payMethod.downlinkFailDesc'),
            variant: 'destructive',
            duration: 14000,
          });
        } else {
          toast({
            title: t('topup.payMethod.confirmedTitle'),
            description: token
              ? t('topup.payMethod.confirmedWithTokenDesc')
              : t('topup.payMethod.confirmedDesc'),
            duration: 10000,
          });
        }
        if (onPaymentConfirmed) onPaymentConfirmed(row);
        if (token || downlinkOk) tokenNotified.current = true;
      } else if ((token || downlinkOk) && !tokenNotified.current) {
        tokenNotified.current = true;
        if (downlinkOk) {
          toast({
            title: t('topup.payMethod.downlinkOkTitle'),
            description: t('topup.payMethod.downlinkOkDesc'),
            duration: 12000,
          });
        } else if (keypadFallback) {
          toast({
            title: t('topup.payMethod.downlinkFailTitle'),
            description: t('topup.payMethod.downlinkFailDesc'),
            variant: 'destructive',
            duration: 14000,
          });
        } else {
          toast({
            title: t('topup.payMethod.stsTokenReadyTitle'),
            description: t('topup.payMethod.stsTokenReadyDesc'),
            duration: 12000,
          });
        }
      }
    }
  };

  // Realtime + poll: webhook marks payment completed first, then may write sts_token_no
  useEffect(() => {
    const awaitingConfirmation = step === 'ussd-success' || step === 'card-success';
    if (!awaitingConfirmation || !orderRef.current) return;

    const ref = orderRef.current;
    confirmedNotified.current = false;
    tokenNotified.current = false;

    const channel = supabase
      .channel(`payment-confirm-${ref}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'clickpesa_payments',
          filter: `order_reference=eq.${ref}`,
        },
        (payload: any) => {
          applyPaymentRow(payload.new);
          if (String(payload.new?.status || '').toLowerCase() === 'failed') {
            toast({
              variant: 'destructive',
              title: t('topup.payMethod.failedTitle'),
              description: t('topup.payMethod.failedDesc'),
              duration: 8000,
            });
          }
        }
      )
      .subscribe();

    // Poll as fallback (realtime can miss events); allow time for LoRaWAN ACK (~45s+)
    let ticks = 0;
    const pollId = window.setInterval(async () => {
      ticks += 1;
      if (ticks > 90) {
        window.clearInterval(pollId);
        return;
      }
      const { data } = await supabase
        .from('clickpesa_payments')
        .select('status, sts_token_no, recharge_status, recharge_channel, order_reference, amount, payment_method')
        .eq('order_reference', ref)
        .maybeSingle();
      if (data) applyPaymentRow(data);
      if (
        data &&
        (String(data.status).toLowerCase() === 'failed' ||
          (['completed', 'success'].includes(String(data.status).toLowerCase()) &&
            (data.sts_token_no ||
              data.recharge_channel ||
              ['confirmed', 'failed'].includes(String(data.recharge_status || '').toLowerCase()))))
      ) {
        window.clearInterval(pollId);
      }
    }, 3000);

    return () => {
      supabase.removeChannel(channel);
      window.clearInterval(pollId);
    };
  }, [step]);

  // ─── Method Selection ───────────────────────────────────────────────────────

  const handleSelectUssd = () => {
    setMethod('ussd');
    setStep('phone');
  };

  const handleSelectCard = async () => {
    setMethod('card');
    setLoading(true);

    // Fetch user profile and current exchange rate in parallel
    const [profileResult, rateResult] = await Promise.all([
      userId
        ? supabase.from('users').select('first_name, last_name, email').eq('id', userId).maybeSingle()
        : Promise.resolve({ data: null }),
      supabase.from('clickpesa_rates').select('rate, margin_pct, card_fee_pct').eq('source', 'USD').eq('target', 'TZS').single(),
    ]);

    if (profileResult.data) {
      const d = profileResult.data as any;
      setUserProfile({
        fullName: [d.first_name, d.last_name].filter(Boolean).join(' '),
        email: d.email || '',
        phone: '',
      });
    }

    // Compute quote client-side for preview (server recomputes authoritatively)
    // ClickPesa adds their fee ON TOP of what we specify — so we send usdBase,
    // and the cardholder pays usdBase × (1 + cardFeePct).
    const rateRow = rateResult.data;
    const rate = rateRow?.rate ?? 2500;
    const marginPct = rateRow?.margin_pct ?? 0.05;
    const cardFeePct = rateRow?.card_fee_pct ?? 0.0463;
    const effectiveRate = rate * (1 - marginPct);
    const usdBase = Math.ceil((amount / effectiveRate) * 100) / 100;
    const usdCharged = Math.ceil((usdBase * (1 + cardFeePct)) * 100) / 100;
    setCardQuote({ exchangeRate: rate, effectiveRate, marginPct, cardFeePct, usdBase, usdCharged });

    setLoading(false);
    setStep('card-confirm');
  };

  // ─── USSD Flow ──────────────────────────────────────────────────────────────

  const handleCheckPaymentMethods = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast({
        variant: 'destructive',
        title: t('topup.payMethod.invalidPhone'),
        description: t('topup.payMethod.invalidPhoneDesc'),
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('clickpesa-preview-ussd-push', {
        body: {
          phoneNumber: phoneNumber.replace(/^\+/, ''),
          amount: amount.toString(),
          fetchSenderDetails: true,
        },
      });

      if (error) throw error;
      setPreviewData(data);
      setStep('preview');
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: t('topup.payMethod.loadMethodsFailed'),
        description: error.message || t('topup.payMethod.loadMethodsFailedDesc'),
      });
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUssd = async () => {
    // Generate orderRef once; reuse on retries (idempotency)
    if (!orderRef.current) {
      orderRef.current = generateOrderRef('ussd');
    }

    setStep('processing');
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('clickpesa-initiate-ussd-push', {
        body: {
          phoneNumber: phoneNumber.replace(/^\+/, ''),
          amount: amount.toString(),
          orderReference: orderRef.current,
          userId,
          meterId,
          adminPayerId,
          source,
        },
      });

      if (error) throw error;
      // 200-with-error guard: the function can return HTTP 200 with a failure
      // body (e.g. { error } / no channel). Never show "Payment Initiated" then.
      if (!data || data.error || data.success === false || !data.channel) {
        throw new Error(data?.error || data?.message || t('topup.payMethod.couldNotInitiate'));
      }

      setTransactionResult(data);
      setStep('ussd-success');
      toast({
        title: t('topup.payMethod.initiatedTitle'),
        description: t('topup.payMethod.initiatedDesc', { channel: data.channel }),
        duration: 8000,
      });
      if (onSuccess) onSuccess(data);
    } catch (error: any) {
      setStep('preview');

      // Handle 409 Duplicate gracefully: payment was already initiated.
      // supabase.functions.invoke() throws before reading the response body on a
      // non-2xx status, so error.context is the raw, unread Response - it has to
      // be awaited via .text(), not read synchronously as error.context.body
      // (an unread ReadableStream, never a JSON string).
      let errorMessage = t('topup.payMethod.initiateFailed');
      if (error.context && typeof error.context.text === 'function') {
        try {
          const text = await error.context.text();
          if (text) {
            const body = JSON.parse(text);
            if (body.error === 'Duplicate order reference') {
              errorMessage = t('topup.payMethod.alreadyInitiatedUssd');
            } else {
              errorMessage = body.details || body.message || body.error || errorMessage;
            }
          }
        } catch { /* use default */ }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({ variant: 'destructive', title: t('topup.payMethod.failedTitle'), description: errorMessage, duration: 8000 });
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Card Flow ──────────────────────────────────────────────────────────────

  const handleConfirmCard = async () => {
    if (!userProfile.fullName || !userProfile.email || !userProfile.phone) {
      toast({
        variant: 'destructive',
        title: t('topup.payMethod.missingCustomer'),
        description: t('topup.payMethod.missingCustomerDesc'),
      });
      return;
    }

    // Generate orderRef once; reuse on retries (idempotency)
    if (!orderRef.current) {
      orderRef.current = generateOrderRef('card');
    }

    setStep('processing');
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('clickpesa-initiate-card-payment', {
        body: {
          amountTzs: amount,          // server converts TZS→USD using live rate
          orderReference: orderRef.current,
          customerFullName: userProfile.fullName,
          customerEmail: userProfile.email,
          customerPhone: userProfile.phone,
          userId,
          meterId,
          adminPayerId,
          source,
        },
      });

      if (error) throw error;
      // 200-with-error guard: don't open window.open(undefined) / claim success
      // when the body came back without a usable payment link.
      if (!data || data.error || data.success === false || !data.cardPaymentLink) {
        throw new Error(data?.error || data?.message || t('topup.payMethod.cardCouldNotStart'));
      }

      const link: string = data.cardPaymentLink;
      setCardPaymentLink(link);
      setStep('card-success');

      // Open the hosted checkout in a new tab
      window.open(link, '_blank', 'noopener,noreferrer');

      toast({
        title: t('topup.payMethod.cardPageOpenedTitle'),
        description: t('topup.payMethod.cardPageOpenedDesc'),
        duration: 10000,
      });
      // NOTE: onSuccess is NOT called here. For card payments, success is confirmed by the
      // ClickPesa webhook → realtime subscription (above). Calling it here would signal
      // "payment done" before the user has even seen the card page.
    } catch (error: any) {
      setStep('card-confirm');

      // error.context is the raw, unread Response (invoke() throws before reading
      // the body on a non-2xx status) - await .text(), not error.context.body
      // (an unread ReadableStream, never a JSON string).
      let errorMessage = t('topup.payMethod.cardInitiateFailed');
      if (error.context && typeof error.context.text === 'function') {
        try {
          const text = await error.context.text();
          if (text) {
            const body = JSON.parse(text);
            if (body.error === 'Duplicate order reference') {
              errorMessage = t('topup.payMethod.alreadyInitiatedCard');
              // Preserve the link if we have it so user can re-open
              if (cardPaymentLink) {
                setStep('card-success');
              }
            } else {
              errorMessage = body.details || body.message || body.error || errorMessage;
            }
          }
        } catch { /* use default */ }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({ variant: 'destructive', title: t('topup.payMethod.cardFailedTitle'), description: errorMessage, duration: 8000 });
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Navigation ─────────────────────────────────────────────────────────────

  const handleBack = () => {
    if (step === 'phone' || step === 'card-confirm') {
      setStep('method');
      setMethod(null);
    } else if (step === 'preview') {
      setStep('phone');
      setPreviewData(null);
    }
  };

  const handleReset = () => {
    resetPaymentSession();
    setPhoneNumber('');
  };

  const copyStsToken = async () => {
    if (!stsTokenNo) return;
    try {
      await navigator.clipboard.writeText(stsTokenNo.replace(/\D/g, '') || stsTokenNo);
      toast({ title: t('topup.payMethod.stsTokenCopied') });
    } catch {
      toast({
        variant: 'destructive',
        title: t('topup.payMethod.stsTokenCopyFailed'),
      });
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Amount header */}
      <div className="flex items-center justify-between px-1">
        <span className="text-sm text-muted-foreground">{t('topup.payMethod.amountToPay')}</span>
        <span className="font-bold text-lg">{formatTSh(amount)}</span>
      </div>

      {/* Step: Method Selection */}
      {step === 'method' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('topup.payMethod.chooseMethod')}</CardTitle>
            <CardDescription>{t('topup.payMethod.howToPay')}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={handleSelectUssd}
              className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center"
            >
              <Smartphone className="h-8 w-8 text-blue-600" />
              <div>
                <p className="font-semibold">{t('topup.payMethod.mobileMoney')}</p>
                <p className="text-xs text-muted-foreground">{t('topup.payMethod.ussdPush')}</p>
              </div>
            </button>
            <button
              onClick={handleSelectCard}
              disabled={loading}
              className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-center disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              ) : (
                <CreditCard className="h-8 w-8 text-purple-600" />
              )}
              <div>
                <p className="font-semibold">{t('topup.payMethod.cardPayment')}</p>
                <p className="text-xs text-muted-foreground">{t('topup.payMethod.mastercard')}</p>
              </div>
            </button>
          </CardContent>
        </Card>
      )}

      {/* Step: USSD — Enter Phone */}
      {step === 'phone' && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <CardTitle>{t('topup.payMethod.enterPhone')}</CardTitle>
                <CardDescription>{t('topup.payMethod.enterPhoneDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">{t('common.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                inputMode="tel"
                placeholder="255696129191"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\s/g, ''))}
                className="text-base"
              />
              <p className="text-xs text-muted-foreground">{t('topup.payMethod.phoneFormat')}</p>
            </div>
            <Button onClick={handleCheckPaymentMethods} disabled={!phoneNumber || loading} className="w-full">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('topup.payMethod.checking')}</> : t('common.continue')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step: USSD — Preview & Confirm */}
      {step === 'preview' && previewData && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <CardTitle>{t('topup.payMethod.confirmPayment')}</CardTitle>
                <CardDescription>{t('topup.payMethod.confirmPaymentDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {previewData.activeMethods && previewData.activeMethods.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">{t('topup.payMethod.availableMethods')}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {previewData.activeMethods.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-muted rounded">
                      <span className="font-medium text-sm">{m.name}</span>
                      <Badge variant={m.status === 'AVAILABLE' ? 'default' : 'secondary'}>
                        {t('topup.payMethod.fee', { amount: formatTSh(m.fee) })}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {previewData.sender && (
              <div className="p-4 bg-muted rounded space-y-2">
                <h4 className="text-sm font-semibold">{t('topup.payMethod.senderDetails')}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">{t('topup.payMethod.senderName')}</span>
                    <p className="font-medium">{previewData.sender.accountName}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('topup.payMethod.senderNumber')}</span>
                    <p className="font-medium">{previewData.sender.accountNumber}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground">{t('topup.payMethod.senderProvider')}</span>
                    <p className="font-medium">{previewData.sender.accountProvider}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="p-4 bg-primary/10 rounded border border-primary/20">
              <div className="flex justify-between items-center">
                <span className="font-medium">{t('topup.payMethod.totalAmount')}</span>
                <span className="text-xl font-bold text-primary">{formatTSh(amount)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t('topup.payMethod.plusNetworkFees')}</p>
            </div>

            <Button onClick={handleConfirmUssd} disabled={loading} className="w-full" size="lg">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('topup.payMethod.processing')}</> : t('topup.payMethod.confirmAndPay')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step: Processing (shared) */}
      {step === 'processing' && (
        <Card>
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <div className="text-center">
                <h4 className="font-semibold">{t('topup.payMethod.processingPayment')}</h4>
                <p className="text-sm text-muted-foreground">{t('topup.payMethod.pleaseWait')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: USSD Success */}
      {step === 'ussd-success' && transactionResult && (
        <Card>
          <CardContent className="py-6">
            <div
              className={`p-4 border rounded space-y-4 ${
                paymentFailed
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  : paymentConfirmed
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              }`}
            >
              <div className="flex items-center gap-2">
                {paymentConfirmed && !paymentFailed ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : paymentFailed ? (
                  <CheckCircle className="h-6 w-6 text-red-600" />
                ) : (
                  <Loader2 className="h-6 w-6 text-green-600 animate-spin" />
                )}
                <h4 className="text-lg font-semibold text-green-900 dark:text-green-100">
                  {paymentFailed
                    ? t('topup.payMethod.failedTitle')
                    : paymentConfirmed
                      ? t('topup.payMethod.paymentConfirmed')
                      : t('topup.payMethod.paymentInitiated')}
                </h4>
              </div>
              <p className="text-sm text-green-800 dark:text-green-200">
                {paymentFailed
                  ? t('topup.payMethod.failedDesc')
                  : paymentConfirmed
                    ? rechargeChannel === 'chirpstack'
                      ? t('topup.payMethod.downlinkOkDesc')
                      : stsTokenNo && rechargeChannel === 'sts_token'
                        ? t('topup.payMethod.downlinkFailDesc')
                        : stsTokenNo
                          ? t('topup.payMethod.enterTokenOnMeter')
                          : rechargeFailed
                            ? t('topup.payMethod.tokenFailedDesc')
                            : rechargePending
                              ? t('topup.payMethod.preparingToken')
                              : t('topup.payMethod.confirmedDesc')
                    : t('topup.payMethod.promptSent')}
              </p>
              <div className="text-sm space-y-1 text-green-800 dark:text-green-200 bg-green-100 dark:bg-green-900/40 p-3 rounded">
                <p><strong>{t('topup.payMethod.transactionId')}</strong> {transactionResult.id}</p>
                <p>
                  <strong>{t('common.status')}:</strong>{' '}
                  {paymentFailed
                    ? 'FAILED'
                    : paymentConfirmed
                      ? 'COMPLETED'
                      : transactionResult.status}
                </p>
                <p><strong>{t('topup.payMethod.channel')}</strong> {transactionResult.channel}</p>
                <p><strong>{t('topup.payMethod.orderRef')}</strong> {transactionResult.orderReference || orderRef.current}</p>
              </div>

              {paymentConfirmed && rechargeFailed && !stsTokenNo && (
                <div className="text-sm text-amber-900 dark:text-amber-100 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded p-3">
                  {t('topup.payMethod.tokenFailedDesc')}
                </div>
              )}

              {paymentConfirmed && rechargePending && !stsTokenNo && !rechargeChannel && (
                <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded p-3">
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  {t('topup.payMethod.preparingToken')}
                </div>
              )}

              {paymentConfirmed && rechargeChannel === 'chirpstack' && (
                <div className="rounded-lg border-2 border-green-500 bg-green-50 dark:bg-green-950/40 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-green-900 dark:text-green-100">
                    <CheckCircle className="h-5 w-5" />
                    <p className="font-semibold">{t('topup.payMethod.downlinkOkTitle')}</p>
                    <Badge className="bg-green-600 text-white ml-auto">{t('topup.payMethod.downlinkOkBadge')}</Badge>
                  </div>
                  <p className="text-sm text-green-800 dark:text-green-200">
                    {t('topup.payMethod.downlinkOkDesc')}
                  </p>
                  {stsTokenNo && (
                    <>
                      <p className="text-xs text-muted-foreground text-center">{t('topup.payMethod.tokenKeepRecord')}</p>
                      <p className="font-mono text-lg tracking-wider text-center break-all">
                        {formatStsTokenGroups(stsTokenNo)}
                      </p>
                      <Button type="button" variant="outline" className="w-full" onClick={copyStsToken}>
                        <Copy className="mr-2 h-4 w-4" />
                        {t('topup.payMethod.copyStsToken')}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {stsTokenNo && rechargeChannel !== 'chirpstack' && (
                <div className="rounded-lg border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/40 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
                    <KeyRound className="h-5 w-5" />
                    <p className="font-semibold">{t('topup.payMethod.downlinkFailTitle')}</p>
                    <Badge className="bg-amber-600 text-white ml-auto">{t('topup.payMethod.keypadBadge')}</Badge>
                  </div>
                  <p className="text-sm text-amber-900 dark:text-amber-100 text-center">
                    {t('topup.payMethod.downlinkFailDesc')}
                  </p>
                  <p className="font-mono text-xl sm:text-2xl tracking-wider text-center text-amber-950 dark:text-amber-50 break-all">
                    {formatStsTokenGroups(stsTokenNo)}
                  </p>
                  <p className="text-xs text-amber-800 dark:text-amber-200 text-center">
                    {t('topup.payMethod.enterTokenOnMeter')}
                  </p>
                  <Button type="button" variant="outline" className="w-full border-amber-400" onClick={copyStsToken}>
                    <Copy className="mr-2 h-4 w-4" />
                    {t('topup.payMethod.copyStsToken')}
                  </Button>
                </div>
              )}

              <Button variant="outline" onClick={handleReset} className="w-full">{t('topup.payMethod.makeAnother')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: Card — Confirm Customer Info */}
      {step === 'card-confirm' && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handleBack} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <CardTitle>{t('topup.payMethod.cardPayment')}</CardTitle>
                <CardDescription>{t('topup.payMethod.cardConfirmDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{t('topup.payMethod.requiredByClickpesa')}</p>
              <div className="space-y-1">
                <Label htmlFor="card-name">{t('topup.payMethod.fullName')} <span className="text-red-500">*</span></Label>
                <Input
                  id="card-name"
                  value={userProfile.fullName}
                  onChange={(e) => setUserProfile(p => ({ ...p, fullName: e.target.value }))}
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="card-email">{t('common.email')} <span className="text-red-500">*</span></Label>
                <Input
                  id="card-email"
                  type="email"
                  value={userProfile.email}
                  onChange={(e) => setUserProfile(p => ({ ...p, email: e.target.value }))}
                  placeholder="john@example.com"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="card-phone">{t('common.phone')} <span className="text-red-500">*</span></Label>
                <Input
                  id="card-phone"
                  type="tel"
                  value={userProfile.phone}
                  onChange={(e) => setUserProfile(p => ({ ...p, phone: e.target.value }))}
                  placeholder="255712345678"
                />
                <p className="text-xs text-muted-foreground">{t('topup.payMethod.phoneFormatCard')}</p>
              </div>
            </div>

            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded border border-purple-200 dark:border-purple-800 space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">{t('topup.payMethod.amountTzs')}</span>
                <span className="font-medium">{formatTSh(amount)}</span>
              </div>
              {cardQuote && (
                <>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">{t('topup.payMethod.rateUsd')}</span>
                    <span className="font-medium">TZS {cardQuote.effectiveRate.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">{t('topup.payMethod.cardFee')}</span>
                    <span className="font-medium">${(cardQuote.usdCharged - cardQuote.usdBase).toFixed(2)}</span>
                  </div>
                  <div className="border-t border-purple-200 dark:border-purple-700 pt-2 flex justify-between items-center">
                    <span className="font-semibold">{t('topup.payMethod.totalCharged')}</span>
                    <span className="text-xl font-bold text-purple-700 dark:text-purple-300">${cardQuote.usdCharged.toFixed(2)} USD</span>
                  </div>
                </>
              )}
              <p className="text-xs text-muted-foreground">
                {t('topup.payMethod.redirectNotice')}
              </p>
            </div>

            <Button
              onClick={handleConfirmCard}
              disabled={loading || !userProfile.fullName || !userProfile.email || !userProfile.phone}
              className="w-full bg-purple-600 hover:bg-purple-700"
              size="lg"
            >
              {loading
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('topup.payMethod.openingPage')}</>
                : <><CreditCard className="mr-2 h-4 w-4" />{t('topup.payMethod.payByCard')}</>
              }
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step: Card Success */}
      {step === 'card-success' && cardPaymentLink && (
        <Card>
          <CardContent className="py-6">
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded space-y-4">
              <div className="flex items-center gap-2">
                {paymentConfirmed ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <CheckCircle className="h-6 w-6 text-purple-600" />
                )}
                <h4 className="text-lg font-semibold text-purple-900 dark:text-purple-100">
                  {paymentConfirmed
                    ? t('topup.payMethod.paymentConfirmed')
                    : t('topup.payMethod.pageOpened')}
                </h4>
              </div>
              <p className="text-sm text-purple-800 dark:text-purple-200">
                {paymentConfirmed
                  ? rechargeChannel === 'chirpstack'
                    ? t('topup.payMethod.downlinkOkDesc')
                    : stsTokenNo && rechargeChannel === 'sts_token'
                      ? t('topup.payMethod.downlinkFailDesc')
                      : stsTokenNo
                        ? t('topup.payMethod.enterTokenOnMeter')
                        : rechargePending
                          ? t('topup.payMethod.preparingToken')
                          : t('topup.payMethod.confirmedDesc')
                  : t('topup.payMethod.completeInTab')}
              </p>
              {!paymentConfirmed && (
                <Button
                  variant="outline"
                  className="w-full border-purple-300 text-purple-700"
                  onClick={() => window.open(cardPaymentLink, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t('topup.payMethod.reopenPage')}
                </Button>
              )}
              {paymentConfirmed && rechargePending && !stsTokenNo && !rechargeChannel && (
                <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded p-3">
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  {t('topup.payMethod.preparingToken')}
                </div>
              )}
              {paymentConfirmed && rechargeChannel === 'chirpstack' && (
                <div className="rounded-lg border-2 border-green-500 bg-green-50 dark:bg-green-950/40 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-green-900 dark:text-green-100">
                    <CheckCircle className="h-5 w-5" />
                    <p className="font-semibold">{t('topup.payMethod.downlinkOkTitle')}</p>
                    <Badge className="bg-green-600 text-white ml-auto">{t('topup.payMethod.downlinkOkBadge')}</Badge>
                  </div>
                  <p className="text-sm text-green-800 dark:text-green-200">
                    {t('topup.payMethod.downlinkOkDesc')}
                  </p>
                  {stsTokenNo && (
                    <>
                      <p className="text-xs text-muted-foreground text-center">{t('topup.payMethod.tokenKeepRecord')}</p>
                      <p className="font-mono text-lg tracking-wider text-center break-all">
                        {formatStsTokenGroups(stsTokenNo)}
                      </p>
                      <Button type="button" variant="outline" className="w-full" onClick={copyStsToken}>
                        <Copy className="mr-2 h-4 w-4" />
                        {t('topup.payMethod.copyStsToken')}
                      </Button>
                    </>
                  )}
                </div>
              )}
              {stsTokenNo && rechargeChannel !== 'chirpstack' && (
                <div className="rounded-lg border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/40 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
                    <KeyRound className="h-5 w-5" />
                    <p className="font-semibold">{t('topup.payMethod.downlinkFailTitle')}</p>
                    <Badge className="bg-amber-600 text-white ml-auto">{t('topup.payMethod.keypadBadge')}</Badge>
                  </div>
                  <p className="text-sm text-amber-900 dark:text-amber-100 text-center">
                    {t('topup.payMethod.downlinkFailDesc')}
                  </p>
                  <p className="font-mono text-xl sm:text-2xl tracking-wider text-center break-all">
                    {formatStsTokenGroups(stsTokenNo)}
                  </p>
                  <Button type="button" variant="outline" className="w-full border-amber-400" onClick={copyStsToken}>
                    <Copy className="mr-2 h-4 w-4" />
                    {t('topup.payMethod.copyStsToken')}
                  </Button>
                </div>
              )}
              <Button variant="outline" onClick={handleReset} className="w-full">
                {t('topup.payMethod.makeAnother')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentMethodSelector;
